/**
 * CognitiveTwinPage — iter600 Cognitive digital twin.
 *
 * Mini-tests: Reaction Time, N-back-2, Mood self-report, Stroop
 * Summary: 30-day timeline + weekly cognitive index + per-test averages
 * Correlations: Sleep vs cognition, Mediterranean adherence vs cognition
 * PDF export via window.print()
 *
 * Free за всички — резултати се пазят за Core+ план (gated server-side).
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { MobileTopBar } from "@/components/MobileTopBar";
import BrainFogCard from "@/components/neuro/BrainFogCard";  // iter1036
import ArousalReactionCard from "@/components/cognitive/ArousalReactionCard";  // iter1038
import MorningRitualCard from "@/components/cognitive/MorningRitualCard";  // iter1039
import { useLanguage } from "@/i18n/LanguageContext";
import {
  Brain, Zap, Target as TargetIcon, Smile, Activity,
  FileText, ArrowRight, RotateCcw, Loader2, TrendingUp,
} from "lucide-react";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";
import DailyCheckinCard from "@/components/shared/DailyCheckinCard";  // iter1054
import { twinLabel } from "@/lib/twinCatalog";

const TESTS = [
  { id: "reaction_time", icon: Zap,       label_bg: "Време на реакция",  duration: "30 сек" },
  { id: "n_back",        icon: TargetIcon, label_bg: "N-back памет",     duration: "60 сек" },
  { id: "mood",          icon: Smile,     label_bg: "Настроение",        duration: "20 сек" },
  { id: "stroop",        icon: Activity,  label_bg: "Stroop тест",       duration: "90 сек" },
];

export default function CognitiveTwinPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [summary, setSummary] = useState(null);
  const [correlations, setCorrelations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTest, setActiveTest] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/cognitive/summary?days=30`),
        axios.get(`${API}/cognitive/correlations?days=30`),
      ]);
      setSummary(s.data);
      setCorrelations(c.data);
    } catch (e) {
      // First-time users will get empty results — not an error
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const onTestComplete = async (testId, payload) => {
    try {
      const sleepHours = window.prompt(
        bg ? "Колко часа спахте снощи? (опционално)" : "Hours slept last night? (optional)"
      );
      const sleep_hours = sleepHours ? parseFloat(sleepHours) : null;
      const { data } = await axios.post(`${API}/cognitive/submit`, {
        test_id: testId,
        ...payload,
        sleep_hours: isNaN(sleep_hours) ? null : sleep_hours,
      });
      // iter602 — Show AI personalized insight if Claude returned one
      if (data.insight_bg) {
        toast.success(
          (bg ? `Резултат: ${data.score}/100 ✓` : `Score: ${data.score}/100 ✓`),
          { description: data.insight_bg, duration: 12000 }
        );
      } else {
        toast.success(bg ? `Резултат: ${data.score}/100 ✓` : `Score: ${data.score}/100 ✓`);
      }
      setActiveTest(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при запис" : "Save failed"));
    }
  };

  const cardCls = isDark ? "bg-slate-900/40 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`min-h-screen p-4 sm:p-6 ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="cognitive-twin-page">
      <MobileTopBar title="Когниция" fallbackTo="/dashboard" testid="neuro-twin-mobile-topbar" />
      <div className="max-w-5xl mx-auto space-y-4">
        {/* iter1017 — профилът захранва близнака */}
        <ProfileContextStrip domain="cognitive" isDark={isDark} className="mb-4" />

        {/* iter1054 — един вход за три близнака */}
        <div className="mb-4">
          <DailyCheckinCard isDark={isDark} />
        </div>
        {/* Header */}
        <div className={`p-4 rounded-2xl border ${cardCls}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{bg ? twinLabel("neuro") : "Cognitive Twin"}</h1>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg
                  ? "4 mini-теста по 30-90 сек. Проследи как храненето и съня влияят върху мозъчната ти производителност."
                  : "4 mini-tests, 30-90 sec each. Track how food and sleep affect your brain performance."}
              </p>
            </div>
            <Button variant="outline" className="ml-auto" onClick={() => window.print()} data-testid="cognitive-pdf-export">
              <FileText className="w-4 h-4 mr-1" /> PDF
            </Button>
          </div>
        </div>

        {/* Active test renderer */}
        {activeTest === "reaction_time" && <ReactionTimeTest onDone={(p) => onTestComplete("reaction_time", p)} onCancel={() => setActiveTest(null)} bg={bg} isDark={isDark} />}
        {activeTest === "n_back" && <NBackTest onDone={(p) => onTestComplete("n_back", p)} onCancel={() => setActiveTest(null)} bg={bg} isDark={isDark} />}
        {activeTest === "mood" && <MoodTest onDone={(p) => onTestComplete("mood", p)} onCancel={() => setActiveTest(null)} bg={bg} isDark={isDark} />}
        {activeTest === "stroop" && <StroopTest onDone={(p) => onTestComplete("stroop", p)} onCancel={() => setActiveTest(null)} bg={bg} isDark={isDark} />}

        {/* Test launcher cards */}
        {!activeTest && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {TESTS.map((t) => {
              const Icon = t.icon;
              const stats = summary?.per_test?.[t.id];
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTest(t.id)}
                  data-testid={`cognitive-start-${t.id}`}
                  className={`text-left p-4 rounded-2xl border transition-all hover:shadow-lg hover:-translate-y-0.5 ${cardCls}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-5 h-5 text-indigo-500" />
                    <span className="font-bold text-sm">{t.label_bg}</span>
                  </div>
                  <p className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"} uppercase`}>{t.duration}</p>
                  {stats?.latest_score != null && (
                    <div className="mt-2 flex items-center justify-between">
                      <span className={`text-2xl font-bold ${stats.latest_score >= 70 ? "text-emerald-500" : stats.latest_score >= 50 ? "text-amber-500" : "text-rose-500"}`}>
                        {stats.latest_score.toFixed(0)}
                      </span>
                      <span className="text-[10px] text-slate-500">/ 100</span>
                    </div>
                  )}
                  <div className="mt-2 inline-flex items-center text-[11px] font-semibold text-indigo-500">
                    {bg ? "Започни" : "Start"} <ArrowRight className="w-3 h-3 ml-1" />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Summary cards */}
        {!loading && summary && (
          <div className={`p-4 rounded-2xl border ${cardCls}`}>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <h2 className="font-bold text-sm">{bg ? "30-дневен преглед" : "30-day overview"}</h2>
              <span className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                {summary.total_sessions} {bg ? "сесии" : "sessions"}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TESTS.map(t => {
                const s = summary.per_test?.[t.id] || {};
                return (
                  <div key={t.id} className={`p-2 rounded-md ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`}>
                    <div className="text-[9px] uppercase font-bold opacity-60">{t.label_bg}</div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-lg font-bold">{s.avg_score != null ? s.avg_score : "—"}</span>
                      <span className="text-[9px] opacity-60">{bg ? "ср." : "avg"}</span>
                    </div>
                    <div className="text-[9px] opacity-50">{s.count || 0} {bg ? "теста" : "tests"}</div>
                  </div>
                );
              })}
            </div>
            {summary.weekly_index?.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase font-bold opacity-60 mb-1">{bg ? "Седмичен когнитивен индекс" : "Weekly cognitive index"}</div>
                <div className="flex items-end gap-1 h-12">
                  {summary.weekly_index.slice(-8).map(w => (
                    <div key={w.week} className="flex-1 flex flex-col items-center" title={`${w.week}: ${w.score}`}>
                      <div
                        className="w-full bg-gradient-to-t from-indigo-500 to-purple-500 rounded-t"
                        style={{ height: `${w.score}%` }}
                      />
                      <span className="text-[8px] opacity-60 mt-0.5">{w.score.toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* iter1038 — будност и реакция (ретикуларна формация) */}
        <ArousalReactionCard isDark={isDark} refreshKey={summary?.total_sessions || 0}
          onStartTest={() => setActiveTest("reaction_time")}
          cardCls={`p-4 rounded-2xl border ${cardCls}`} />

        {/* iter1039 — сутрешен ритуал: светлина, вода, движение */}
        <MorningRitualCard isDark={isDark}
          cardCls={`p-4 rounded-2xl border ${cardCls}`} />

        {/* iter1036 — мозъчна мъгла срещу DIL, сън и тестовете */}
        <BrainFogCard isDark={isDark} cardCls={`p-4 rounded-2xl border ${cardCls}`} />

        {/* Correlations */}
        {!loading && correlations && (
          <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="cognitive-correlations">
            <h2 className="font-bold text-sm mb-3">🔗 {bg ? "Корелации" : "Correlations"}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CorrelationCard
                title={bg ? "💤 Сън vs Когниция" : "💤 Sleep vs Cognition"}
                data={correlations.sleep_vs_cognition}
                isDark={isDark}
                bg={bg}
              />
              <CorrelationCard
                title={bg ? "🌿 Средиземноморска адхеренция vs Когниция" : "🌿 Mediterranean vs Cognition"}
                data={correlations.mediterranean_vs_cognition}
                isDark={isDark}
                bg={bg}
              />
            </div>
          </div>
        )}

        {loading && <div className="flex items-center justify-center p-8"><Loader2 className="animate-spin w-6 h-6" /></div>}
      </div>
    </div>
  );
}


// ─────────────────── Test components ───────────────────

function ReactionTimeTest({ onDone, onCancel, bg, isDark }) {
  const [phase, setPhase] = useState("ready"); // ready | wait | go | done
  const [times, setTimes] = useState([]);
  const [errors, setErrors] = useState(0);
  const startRef = useRef(0);

  const startTrial = () => {
    setPhase("wait");
    const delay = 1200 + Math.random() * 2200;
    setTimeout(() => {
      startRef.current = performance.now();
      setPhase("go");
    }, delay);
  };

  const handleClick = () => {
    if (phase === "wait") { setErrors(e => e + 1); setPhase("ready"); return; }
    if (phase === "go") {
      const rt = performance.now() - startRef.current;
      const newTimes = [...times, rt];
      setTimes(newTimes);
      if (newTimes.length >= 5) {
        const mean = newTimes.reduce((a, b) => a + b) / newTimes.length;
        onDone({ duration_ms: Math.round(mean), errors, raw: { trials: newTimes.map(Math.round) } });
        setPhase("done");
      } else {
        setPhase("ready");
      }
    }
  };

  return (
    <TestShell title={bg ? "Време на реакция" : "Reaction Time"} onCancel={onCancel} isDark={isDark}>
      <p className={`text-center text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        {bg ? "Натисни ВЕДНАГА щом видиш зелено · 5 опита" : "Click as soon as you see GREEN · 5 trials"}
      </p>
      <button
        onClick={phase === "ready" ? startTrial : handleClick}
        data-testid="rt-action"
        className={`w-full h-48 rounded-2xl text-xl font-bold transition-colors ${
          phase === "ready" ? "bg-slate-300 text-slate-700"
          : phase === "wait" ? "bg-rose-500 text-white"
          : phase === "go" ? "bg-emerald-500 text-white"
          : "bg-indigo-500 text-white"
        }`}
      >
        {phase === "ready" && (bg ? `Натисни за старт (${times.length}/5)` : `Click to start (${times.length}/5)`)}
        {phase === "wait" && (bg ? "Изчакай..." : "Wait...")}
        {phase === "go" && (bg ? "СЕГА!" : "NOW!")}
        {phase === "done" && "✓"}
      </button>
      {times.length > 0 && (
        <p className="text-center mt-3 text-sm">
          {bg ? "Опити" : "Trials"}: {times.map(t => `${Math.round(t)}ms`).join(" · ")}
        </p>
      )}
    </TestShell>
  );
}


function NBackTest({ onDone, onCancel, bg, isDark }) {
  const [phase, setPhase] = useState("ready");
  const [sequence, setSequence] = useState([]);
  const [current, setCurrent] = useState(null);
  const [idx, setIdx] = useState(0);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [false_alarms, setFA] = useState(0);
  const responded = useRef(false);

  const start = () => {
    const seq = Array.from({ length: 20 }, () => Math.floor(Math.random() * 9) + 1);
    // Force some matches: 30% of trials should be 2-back matches
    for (let i = 2; i < seq.length; i++) {
      if (Math.random() < 0.3) seq[i] = seq[i - 2];
    }
    setSequence(seq); setIdx(0); setHits(0); setMisses(0); setFA(0);
    setPhase("running");
    runStep(seq, 0);
  };

  const runStep = (seq, i) => {
    if (i >= seq.length) {
      finish(seq);
      return;
    }
    responded.current = false;
    setCurrent(seq[i]);
    setIdx(i);
    setTimeout(() => {
      // Evaluate
      const isMatch = i >= 2 && seq[i] === seq[i - 2];
      if (isMatch && !responded.current) setMisses(m => m + 1);
      runStep(seq, i + 1);
    }, 2500);
  };

  const finish = (seq) => {
    const totalMatches = seq.filter((v, i) => i >= 2 && seq[i] === seq[i - 2]).length;
    const accuracy = totalMatches > 0 ? (hits / totalMatches) * 100 : 0;
    const adjusted = Math.max(0, accuracy - false_alarms * 5);
    onDone({ accuracy: Math.round(adjusted * 10) / 10, errors: misses + false_alarms, raw: { hits, misses, false_alarms, total_matches: totalMatches } });
    setPhase("done");
  };

  const handleMatch = () => {
    if (phase !== "running" || responded.current) return;
    responded.current = true;
    const isMatch = idx >= 2 && sequence[idx] === sequence[idx - 2];
    if (isMatch) setHits(h => h + 1);
    else setFA(f => f + 1);
  };

  return (
    <TestShell title="N-back (2-back)" onCancel={onCancel} isDark={isDark}>
      <p className={`text-center text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        {bg ? "Натисни „Съвпада\", когато текущата цифра е същата като ПРЕДИ 2 стъпки. 20 опита." : "Press 'Match' when the digit is the SAME as 2 trials ago. 20 trials."}
      </p>
      {phase === "ready" && (
        <Button onClick={start} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="nback-start">
          {bg ? "Започни" : "Start"}
        </Button>
      )}
      {phase === "running" && (
        <>
          <div className={`w-full h-48 rounded-2xl flex items-center justify-center text-7xl font-bold ${isDark ? "bg-slate-800 text-indigo-300" : "bg-indigo-100 text-indigo-700"}`} data-testid="nback-digit">
            {current}
          </div>
          <Button onClick={handleMatch} className="w-full mt-3 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="nback-match">
            {bg ? "Съвпада ↺2" : "Match ↺2"}
          </Button>
          <p className="text-center mt-2 text-xs opacity-60">{idx + 1} / {sequence.length}</p>
        </>
      )}
    </TestShell>
  );
}


function MoodTest({ onDone, onCancel, bg, isDark }) {
  const [dims, setDims] = useState({ energy: 3, calm: 3, focus: 3, mood: 3 });
  const labels = {
    energy: bg ? "⚡ Енергия" : "⚡ Energy",
    calm:   bg ? "🧘 Спокойствие" : "🧘 Calm",
    focus:  bg ? "🎯 Концентрация" : "🎯 Focus",
    mood:   bg ? "😊 Настроение" : "😊 Mood",
  };
  return (
    <TestShell title={bg ? "Настроение" : "Mood"} onCancel={onCancel} isDark={isDark}>
      <p className={`text-center text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        {bg ? "Оцени всеки аспект (1=ниско, 5=отлично)" : "Rate each (1=low, 5=excellent)"}
      </p>
      <div className="space-y-3">
        {Object.keys(dims).map(d => (
          <div key={d}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span>{labels[d]}</span>
              <span className="font-bold">{dims[d]}</span>
            </div>
            <input
              type="range" min="1" max="5" value={dims[d]}
              onChange={(e) => setDims({ ...dims, [d]: Number(e.target.value) })}
              className="w-full accent-indigo-500"
              data-testid={`mood-${d}`}
            />
          </div>
        ))}
      </div>
      <Button onClick={() => onDone({ mood_dims: dims })} className="w-full mt-4 bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="mood-submit">
        {bg ? "Запази" : "Save"}
      </Button>
    </TestShell>
  );
}


function StroopTest({ onDone, onCancel, bg, isDark }) {
  const COLORS = [
    { name: "червен", en: "red", hex: "#ef4444" },
    { name: "син", en: "blue", hex: "#3b82f6" },
    { name: "зелен", en: "green", hex: "#22c55e" },
    { name: "жълт", en: "yellow", hex: "#eab308" },
  ];
  const [trials] = useState(() => Array.from({ length: 20 }, () => {
    const word = COLORS[Math.floor(Math.random() * 4)];
    const ink = COLORS[Math.floor(Math.random() * 4)];
    return { word, ink };
  }));
  const [idx, setIdx] = useState(0);
  const [times, setTimes] = useState([]);
  const [correct, setCorrect] = useState(0);
  const start = useRef(0);

  useEffect(() => { start.current = performance.now(); }, [idx]);

  const handleAnswer = (chosen) => {
    const rt = performance.now() - start.current;
    const isCorrect = chosen.en === trials[idx].ink.en;
    setTimes([...times, rt]);
    if (isCorrect) setCorrect(c => c + 1);
    if (idx + 1 >= trials.length) {
      const meanRT = (times.length > 0 ? (times.reduce((a, b) => a + b) + rt) / (times.length + 1) : rt);
      const accuracy = ((correct + (isCorrect ? 1 : 0)) / trials.length) * 100;
      onDone({ duration_ms: Math.round(meanRT), accuracy: Math.round(accuracy * 10) / 10, errors: trials.length - correct - (isCorrect ? 1 : 0) });
    } else {
      setIdx(i => i + 1);
    }
  };

  const t = trials[idx];
  return (
    <TestShell title="Stroop" onCancel={onCancel} isDark={isDark}>
      <p className={`text-center text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        {bg ? "Избери ЦВЕТА на думата (НЕ значението)." : "Pick the COLOR of the text (NOT its meaning)."}
      </p>
      <div className={`w-full h-32 rounded-2xl flex items-center justify-center text-4xl font-bold ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
        <span style={{ color: t.ink.hex }}>{bg ? t.word.name.toUpperCase() : t.word.en.toUpperCase()}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        {COLORS.map(c => (
          <button key={c.en} onClick={() => handleAnswer(c)} data-testid={`stroop-${c.en}`}
            className="py-3 rounded-xl font-bold text-white" style={{ backgroundColor: c.hex }}>
            {bg ? c.name : c.en}
          </button>
        ))}
      </div>
      <p className="text-center mt-2 text-xs opacity-60">{idx + 1} / {trials.length}</p>
    </TestShell>
  );
}


function TestShell({ title, children, onCancel, isDark }) {
  return (
    <div className={`p-4 rounded-2xl border ${isDark ? "bg-slate-900/40 border-slate-800" : "bg-white border-slate-200"}`}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold">{title}</h2>
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="test-cancel">
          <RotateCcw className="w-4 h-4" />
        </Button>
      </div>
      {children}
    </div>
  );
}


function CorrelationCard({ title, data, isDark, bg }) {
  if (!data || data.samples < 3) {
    return (
      <div className={`p-3 rounded-xl border ${isDark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"}`}>
        <h3 className="text-sm font-bold mb-1">{title}</h3>
        <p className="text-[11px] opacity-60">{bg ? `Нужни са поне 3 точки данни (имаш ${data?.samples || 0})` : `Need at least 3 data points (have ${data?.samples || 0})`}</p>
      </div>
    );
  }
  const r = data.pearson_r ?? 0;
  const color = r > 0.25 ? "text-emerald-500" : r < -0.25 ? "text-rose-500" : "text-slate-500";
  return (
    <div className={`p-3 rounded-xl border ${isDark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"}`}>
      <h3 className="text-sm font-bold mb-1">{title}</h3>
      <div className={`text-2xl font-bold ${color}`}>r = {r.toFixed(2)}</div>
      <p className="text-[11px] opacity-70 mt-1">{data.interpretation}</p>
      <p className="text-[10px] opacity-50 mt-1">{data.samples} {bg ? "точки данни" : "data points"}</p>
    </div>
  );
}
